import { FormProps, FormRenderProps } from 'react-final-form';
import { PartialDeep } from 'type-fest';

import { ProductGroup, Award_Per_Counting_Strategy } from '@occc/graphql';
import {
  FulfillmentChannelFormValues,
  FulfillmentWindowFormValues,
  LocationInStateConstraintFormValues,
  PaymentMethodFormValues,
  StoreLocationOrGroupsFormValues,
  SubBrandConstraintFormValues,
  VoucherStackingFormValues,
} from '@occc/feat-constraints';
import { PromptAtPumpFormValues } from '@occc-app/routes/CampaignComponents/PromptAtPump/types';
import { CustomDataFormValues } from '@occc-app/routes/CampaignComponents/components/CustomData/types';
import { AwardPointsOrVouchersFormValues } from '@occc-app/offerHelpers/types';
import { ConfigurationFormNameAndDescriptionValues } from '@occc-app/routes/Offer/types';

import { LimitsInConstraintGroupFormValue } from '@occc/feat-limits';
import { DesignFormValues } from './schemas';

import { ScheduleDatesFormValues } from '../CampaignComponents/components/ScheduleDates/schemas';

export enum TRANSACTION_DISCOUNT_TYPE {
  PERCENT = 'PERCENT',
  DOLLARS = 'DOLLARS',
}

export enum PRODUCT_DISCOUNT_TYPE {
  FREE = 'FREE',
  DISCOUNTED = 'DISCOUNTED',
  OVERRIDE = 'OVERRIDE',
}

export enum AWARD_TYPES {
  PRODUCT = 'productDiscount',
  TRANSACTION = 'transactionDiscount',
  SWEEPSTAKES = 'sweepstakesEntry',
  FUEL_ROLLBACK = 'fuelRollback',
  EXTERNAL_FULFILLMENT = 'externalFulfillment',
}

export enum CONFIGURE_PAGE_STATE {
  CONFIGURE = 'CONFIGURE',
  TARGET = 'TARGET',
  DESIGN = 'DESIGN',
  FINALIZE = 'FINALIZE',
}

export type RequiresVoucherConstraintType =
  | 'ConstraintRequiresVoucherAvailableForDiscount'
  | 'ConstraintRequiresVoucher'
  | 'ConstraintRequiresTransactionVoucher';

type EarningMechanismFormValues = {
  awardType: AWARD_TYPES;
  transactionDiscount?: TransactionDiscountFormValues;
  productDiscount?: ProductDiscountFormValues;
  fuelRollback?: FuelRollbackFormValues;
  externalFulfillment?: ExternalFulfillmentFormValues;
};

export type EarningMechanismInitialFormValues = Partial<EarningMechanismFormValues>;

export type TransactionDiscountFormValues = {
  amount: number;
  type: TRANSACTION_DISCOUNT_TYPE;
  productGroups: Pick<ProductGroup, 'id' | 'name'>[];
  purchaseAmountThreshold: number;
};

export type ProductDiscountFormValues = {
  amount: string;
  type: PRODUCT_DISCOUNT_TYPE;
  productGroups: Pick<ProductGroup, 'id' | 'name'>[];
  partialDiscount: boolean;
  discountType: Award_Per_Counting_Strategy;
  allowCarryOver?: boolean;
};

export type FuelRollbackFormValues = {
  amount: number;
  productGroups: Pick<ProductGroup, 'id' | 'name'>[];
};

export type ExternalFulfillmentFormValues = AwardPointsOrVouchersFormValues;

type BaseConfigureFormValues = FulfillmentWindowFormValues &
  FulfillmentChannelFormValues &
  VoucherStackingFormValues &
  PaymentMethodFormValues &
  PromptAtPumpFormValues &
  ConfigurationFormNameAndDescriptionValues & {
    awardCount: LimitsInConstraintGroupFormValue;
    customData?: CustomDataFormValues;
    stackable?: boolean;
    displayForOrderAhead?: boolean;
  };

export type TransactionDiscountValues = {
  awardType: AWARD_TYPES.TRANSACTION;
  transactionDiscount: TransactionDiscountFormValues;
} & BaseConfigureFormValues;

export type ProductDiscountValues = {
  awardType: AWARD_TYPES.PRODUCT;
  productDiscount: ProductDiscountFormValues;
} & BaseConfigureFormValues;

export type FuelRollbackValues = {
  awardType: AWARD_TYPES.FUEL_ROLLBACK;
  fuelRollback: FuelRollbackFormValues;
  voucherStackingConstraint: VoucherStackingFormValues;
} & BaseConfigureFormValues;

export type ExternalFulfillmentValues = {
  awardType: AWARD_TYPES.EXTERNAL_FULFILLMENT;
  externalFulfillment: ExternalFulfillmentFormValues;
} & BaseConfigureFormValues;

export type SweepstakeValues = {
  awardType: AWARD_TYPES.SWEEPSTAKES;
} & BaseConfigureFormValues;

export type ConfigureFormValues =
  | TransactionDiscountValues
  | ProductDiscountValues
  | FuelRollbackValues
  | ExternalFulfillmentValues
  | SweepstakeValues;

export type ConfigureFormInitialValues = PartialDeep<ConfigureFormValues>;
export type ConfigureFormProps = FormProps<ConfigureFormValues>;
export type ConfigureFormRenderProps = FormRenderProps<ConfigureFormValues>;

export type TargetFormValues = LocationInStateConstraintFormValues &
  StoreLocationOrGroupsFormValues &
  SubBrandConstraintFormValues;
export type TargetFormProps = FormProps<TargetFormValues>;
export type TargetFormRenderProps = FormRenderProps<TargetFormValues>;

export type DesignFormProps = FormProps<DesignFormValues>;
export type DesignFormRenderProps = FormRenderProps<DesignFormValues>;

export type FinalizeFormValues = ScheduleDatesFormValues;
export type FinalizeFormInitialValues = Partial<FinalizeFormValues>;
export type FinalizeFormProps = FormProps<FinalizeFormValues, FinalizeFormInitialValues>;
export type FinalizeFormRenderProps = FormRenderProps<
  FinalizeFormValues,
  FinalizeFormInitialValues
>;
